import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { userKeys } from "@/lib/query";

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  banned: boolean | null;
  department: string | null;
  costRate: number | null;
  employeeId: string | null;
  lastLoginAt: string | Date | null;
  createdAt: Date;
}

export function useUsers(filters: { search?: string }) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: async (): Promise<User[]> => {
      const { data, error } = await authClient.admin.listUsers({
        query: {
          limit: 200,
          ...(filters.search
            ? {
                searchField: "email" as const,
                searchValue: filters.search,
                searchOperator: "contains" as const,
              }
            : {}),
        },
      });
      if (error) throw error;
      return (data?.users as User[]) ?? [];
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      email: string;
      password: string;
      role: string;
      department?: string;
      costRate?: number;
      employeeId?: string;
    }) => {
      const { error } = await authClient.admin.createUser({
        name: input.name,
        email: input.email,
        password: input.password,
        role: input.role,
        data: {
          department: input.department ?? null,
          costRate: input.costRate ?? null,
          employeeId: input.employeeId ?? null,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User created");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to create user");
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      userId: string;
      name?: string;
      role?: string;
      department?: string | null;
      costRate?: number | null;
      employeeId?: string | null;
    }) => {
      const { userId, role, ...rest } = input;
      const errors: Error[] = [];

      if (role !== undefined) {
        const { error } = await authClient.admin.setRole({ userId, role });
        if (error) errors.push(error);
      }

      const patchFields = {
        name: rest.name,
        department: rest.department,
        costRate: rest.costRate,
        employeeId: rest.employeeId,
      };
      const hasPatch = Object.values(patchFields).some((v) => v !== undefined);
      if (hasPatch) {
        const res = await fetch(`/api/users/${userId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patchFields),
        });
        if (!res.ok) errors.push(new Error("Failed to update user profile"));
      }

      if (errors.length > 0) throw errors[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to update user");
    },
  });
}

export function useChangeRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; role: string }) => {
      const { error } = await authClient.admin.setRole(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Role updated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to change role");
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.banUser({
        userId,
        banReason: "Deactivated by administrator",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User deactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to deactivate user");
    },
  });
}

export function useReactivateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await authClient.admin.unbanUser({ userId });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("User reactivated");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reactivate user");
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: string; newPassword: string }) => {
      const { error } = await authClient.admin.setUserPassword(input);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      toast.success("Password reset");
    },
    onError: (error: Error) => {
      toast.error(error.message ?? "Failed to reset password");
    },
  });
}
